import { useSelectedLocation } from 'contexts';
import { useResponse } from 'contexts/response';
import { useCallback } from 'react';
import { Customer, EdiCustomerLocation, EDIType } from 'types';
import { FilterOperator, FilterState, filtersToRsqlString, isCustomer, LogicalOperator } from 'utils';
const BASE_URL = new URL('/api/customers', window.location.origin);

export const useCustomerAPI = () => {
  const { handleError, setSuccess } = useResponse();
  const { selectedLocation } = useSelectedLocation();

  const resendBulkEdi = async (
    customerId: number,
    ediType: EDIType,
    fromDate: string,
    toDate: string
  ): Promise<void> => {
    const response = await fetch(
      `/api/customers/${customerId}/resend-edi/${ediType}?fromDate=${fromDate}&toDate=${toDate}`,
      {
        method: 'POST',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      }
    );

    if (response.ok) setSuccess('EDI resent');
    else handleError(response.status + ' ' + response.statusText);
  };

  const resolveErrorStatus = useCallback(
    (response: Response, json: any) => {
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          handleError(json.message ?? response.statusText);
          return null;
      }
    },
    [handleError]
  );

  const getAllCustomers = useCallback(
    async (locationId: Number, includeDeactivated: boolean): Promise<Customer[] | null> => {
      const options: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const url = new URL(BASE_URL, window.location.origin);
        if (!!locationId) {
          url.searchParams.append('locationId', locationId.toString());
        }
        url.searchParams.append('includeDeactivated', includeDeactivated.toString());
        const response = await fetch(url, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok) {
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus]
  );

  const searchCustomers = useCallback(
    /* `locationId` overrides the header's location, for screens bound to a location of their own */
    async (input: string, filterByUserAssociations = true, locationId?: number): Promise<Customer[]> => {
      const options: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      const url = new URL('/api/customers/search', window.location.origin);

      const filter: FilterState = {
        logicalOperator: LogicalOperator.AND,
        filters: input
          .split(/[^a-zA-Z0-9]/)
          .map(value => ({ field: 'name', value, filterOperator: FilterOperator.CONTAINS }))
      };

      url.searchParams.append('filterString', filtersToRsqlString(filter));
      const scope = locationId ?? selectedLocation?.id;
      if (scope !== undefined) {
        url.searchParams.append('locationId', scope.toString());
      }
      url.searchParams.append('filterByUserAssociations', filterByUserAssociations.toString());

      try {
        const response = await fetch(`${url}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok) {
          return json;
        }

        resolveErrorStatus(response, json);
        return [];
      } catch (e) {
        handleError(e);
        return [];
      }
    },
    [handleError, resolveErrorStatus, selectedLocation]
  );

  const getCustomerById = useCallback(
    async (id: number): Promise<Customer | null> => {
      const options: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${BASE_URL}/${id}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok) {
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus]
  );

  const createCustomer = useCallback(
    async (customer: Partial<Customer>): Promise<Customer | null> => {
      const options: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(customer)
      };

      try {
        const response = await fetch(BASE_URL, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Customer Created!');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  const updateCustomerStatus = useCallback(
    async (customerId: number, deactivate: boolean): Promise<Customer | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${BASE_URL}/${deactivate ? 'deactivate' : 'activate'}/${customerId}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess(`Customer ${deactivate ? 'deactivated' : 'activated'}.`);
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  const updateCustomer = useCallback(
    async (customer: Customer): Promise<Customer | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(customer)
      };

      try {
        const response = await fetch(`${BASE_URL}/${customer.id}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Customer Updated!');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  const updateEdiRecipients = useCallback(
    async (customerId: number, ediRecipients: EdiCustomerLocation[]): Promise<Customer | null> => {
      try {
        const response = await fetch(`${BASE_URL}/${customerId}/edi-recipients`, {
          method: 'PUT',
          headers: new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }),
          body: JSON.stringify(ediRecipients)
        });
        if (!response.ok) {
          handleError(response);
          return null;
        }

        const json = await response.json();
        if (!isCustomer(json)) {
          await handleError(response, json);
          return null;
        }

        setSuccess('Recipients Updated!');
        return json;
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const validateCode = useCallback(
    async (code: string, locationId: number): Promise<boolean> => {
      const options: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${BASE_URL}/validation/code/${code}?locationId=${locationId}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok) {
          return json;
        }

        resolveErrorStatus(response, json);
        return false;
      } catch (e) {
        handleError(e);
        return false;
      }
    },
    [handleError, resolveErrorStatus]
  );

  return {
    getAllCustomers,
    searchCustomers,
    getCustomerById,
    createCustomer,
    updateCustomerStatus,
    updateCustomer,
    updateEdiRecipients,
    validateCode,
    resendBulkEdi
  };
};
